import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useParams, Link } from "react-router-dom";
import { useGetStoreQuery } from "../query/storesQuery";
import { formatDate, toDateString } from "../utils/dateUtils";
import { useGetResellerQuery } from "../query/resellersQuery";
import { useGetChainQuery } from "../query/chainsQuery";
import { ActivePackageSection } from "./components/packageSelection/ActivePackageSection";
import { ActiveAddonsSection } from "./components/packageSelection/ActiveAddonsSection";
import { AcquirersSection } from "./components/packageSelection/AcquirersSection";
import { ExtraDevicesSection } from "./components/packageSelection/ExtraDevicesSection";
import { PaymentTerminalsSection } from "./components/packageSelection/PaymentTerminalsSection";
import { EntitlementsSection } from "./components/packageSelection/EntitlementsSection";
import { MonthlyCostSummarySection } from "./components/packageSelection/MonthlyCostSummarySection";
import { useGetBillingItemsQuery } from "../query/billingItemsQuery";
import { useIsAdmin, useIsReseller } from "../hooks/useIsAdmin";
import { useGetStoreAvailableBillingItemsQuery } from "../query/availableBillingItemsQuery";
import { DailyStatsLineChart } from "./components/DailyStatsLineChart";
import { FirestoreConsoleButton } from "../components/FirestoreConsoleButton";
import { GcpLogsConsoleButton } from "../components/GcpLogsConsoleButton";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { getQuantityOnDate } from "../utils/billingItemSegments";

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.x3}px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${spacing.x2}px;
  }
`;

const InfoCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const InfoLabel = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: ${fontSizes.large}px;
  font-weight: 500;
  color: ${colors.text};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const ChainLink = styled(Link)`
  font-size: ${fontSizes.large}px;
  font-weight: 500;
  color: ${colors.primary};
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const StoreOverviewActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
  align-items: center;
`;


export const StoreOverview = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const today = toDateString(new Date());
  const isAdmin = useIsAdmin();
  const isResellerAdmin = useIsReseller();
  const canViewBilling = isAdmin || isResellerAdmin;
  const { data: store } = useGetStoreQuery(storeId);
  const { data: reseller } = useGetResellerQuery(store?.resellerId || undefined);
  const { data: chain } = useGetChainQuery(store?.chainId || undefined);
  const { data: storeAvailableBillingItems } =
    useGetStoreAvailableBillingItemsQuery(
      canViewBilling ? storeId ?? null : null
    );
  const { data: billingItems, isLoading: isLoadingBillingItems } =
    useGetBillingItemsQuery(canViewBilling ? storeId ?? null : null);

  const activePackageItem =
    (billingItems ?? []).find(
      (billingItem) =>
        billingItem.type === BillingItemType.PACKAGE &&
        getQuantityOnDate(
          [billingItem],
          billingItem.availableBillingItemId,
          today
        ) > 0
    ) ?? null;
  const billingPeriod =
    activePackageItem?.billingPeriod ?? BillingPeriod.MONTHLY;
  const startDate = today;

  if (!store) {
    return null;
  }
  return (
    <Container>
      {isAdmin && (
        <StoreOverviewActions>
          <FirestoreConsoleButton path={`stores/${storeId ?? ""}`} />
          <GcpLogsConsoleButton searchId={storeId ?? ""} />
        </StoreOverviewActions>
      )}
      <DailyStatsLineChart storeId={storeId ?? undefined} />
      <DashboardGrid>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("overview.storeName")}</InfoLabel>
            <InfoValue>{store.name}</InfoValue>
          </InfoRow>
          {store.displayName && (
            <InfoRow>
              <InfoLabel>{t("overview.displayName")}</InfoLabel>
              <InfoValue>{store.displayName}</InfoValue>
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>{t("overview.organizationNumber")}</InfoLabel>
            <InfoValue>{store.orgNumber}</InfoValue>
          </InfoRow>
        </InfoCard>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("overview.address")}</InfoLabel>
            <InfoValue>
              {store.street}
              <br />
              {store.postalcode} {store.city}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("overview.country")}</InfoLabel>
            <InfoValue>{store.country}</InfoValue>
          </InfoRow>
        </InfoCard>
        {(store.contactPerson || store.contactEmail || store.contactPhone) && (
          <InfoCard>
            {store.contactPerson && (
              <InfoRow>
                <InfoLabel>{t("overview.contactPerson")}</InfoLabel>
                <InfoValue>{store.contactPerson}</InfoValue>
              </InfoRow>
            )}
            {store.contactEmail && (
              <InfoRow>
                <InfoLabel>{t("overview.contactEmail")}</InfoLabel>
                <InfoValue>{store.contactEmail}</InfoValue>
              </InfoRow>
            )}
            {store.contactPhone && (
              <InfoRow>
                <InfoLabel>{t("overview.contactPhone")}</InfoLabel>
                <InfoValue>{store.contactPhone}</InfoValue>
              </InfoRow>
            )}
          </InfoCard>
        )}
        <InfoCard>
          {store.chainId && (
            <InfoRow>
              <InfoLabel>{t("overview.chain")}</InfoLabel>
              {chain ? (
                <ChainLink to={`/chains/${store.chainId}/overview`}>
                  {chain.name}
                </ChainLink>
              ) : (
                <InfoValue>{store.chainId}</InfoValue>
              )}
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>{t("overview.reseller")}</InfoLabel>
            <InfoValue>{reseller?.name || store.resellerId || t("overview.noReseller")}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("overview.createdAt")}</InfoLabel>
            <InfoValue>{formatDate(store.createdAt)}</InfoValue>
          </InfoRow>
        </InfoCard>
      </DashboardGrid>
      {canViewBilling && (
        <>
          <ActivePackageSection
            billingItems={billingItems}
            availableBillingItems={storeAvailableBillingItems}
            isLoading={isLoadingBillingItems}
            billingPeriod={billingPeriod}
            startDate={startDate}
          />
          <ActiveAddonsSection
            billingItems={billingItems}
            availableBillingItems={storeAvailableBillingItems}
            isLoading={isLoadingBillingItems}
            billingPeriod={billingPeriod}
            startDate={startDate}
          />
          <AcquirersSection />
          <PaymentTerminalsSection
            billingItems={billingItems}
            availableBillingItems={storeAvailableBillingItems}
            isLoading={isLoadingBillingItems}
            billingPeriod={billingPeriod}
            startDate={startDate}
          />
          <ExtraDevicesSection
            billingItems={billingItems}
            startDate={startDate}
          />
          <MonthlyCostSummarySection
            billingItems={billingItems}
            availableBillingItems={storeAvailableBillingItems}
            isLoading={isLoadingBillingItems}
          />
        </>
      )}
      <EntitlementsSection />

    </Container>
  );
};

